import { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { net } from '../../../../database';
import { UserContext } from '../../../../Context';

/**
 * Custom hook for managing pipeline leads with API persistence.
 * Provides CRUD operations, status history tracking, duplicate phone detection,
 * and derived data for the marketing pipeline.
 *
 * Falls back to localStorage when API is unavailable.
 *
 * @returns {Object} Pipeline data and operations
 * @author Forge, opencode glm-5.1
 * @version 2.0
 * @since 2026-04-15
 */

const STORAGE_KEY = 'htx-pipeline-leads';

const STAGES = [
  { id: 'lead', label: 'Lead', color: '#64748b', bg: '#f1f5f9', headerBg: '#334155' },
  { id: 'add-zalo', label: 'Add Zalo', color: '#3b82f6', bg: '#eff6ff', headerBg: '#1e40af' },
  { id: 'book-test', label: 'Book Test', color: '#f59e0b', bg: '#fffbeb', headerBg: '#92400e' },
  { id: 'test', label: 'Test', color: '#f97316', bg: '#fff7ed', headerBg: '#9a3412' },
  { id: 'enrol', label: 'Enrol', color: '#10b981', bg: '#ecfdf5', headerBg: '#065f46' },
];

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
}

function loadFromStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveToStorage(leads) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(leads));
  } catch {
    // silently fail
  }
}

function normalizeLead(apiLead) {
  if (!apiLead) return null;
  return {
    ...apiLead,
    id: apiLead._id || apiLead.id,
    contacts: apiLead.contacts || [],
    children: apiLead.children || [],
    statusHistory: apiLead.statusHistory || [],
  };
}

/**
 * Hook for managing marketing pipeline leads data.
 * Attempts API-first persistence, falls back to localStorage.
 *
 * @returns {Object} { leads, stages, addLead, updateLead, deleteLead, moveLead, addContact, checkPhone, stats, searchQuery, setSearchQuery, filteredLeads, loading, availableCount, pullNextLead }
 * @author Forge, opencode glm-5.1
 * @version 2.2
 * @since 2026-04-15
 */
export default function usePipelineData() {
  const { user } = useContext(UserContext);
  const [leads, setLeads] = useState(loadFromStorage);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [availableCount, setAvailableCount] = useState(null);

  const userId = user?._id || '';

  // Fetch leads from API on mount
  useEffect(() => {
    let mounted = true;
    const fetchLeads = async () => {
      try {
        const data = await net.get('/pipeline/leads');
        if (mounted && data?.leads) {
          const normalized = data.leads.map(normalizeLead);
          setLeads(normalized);
          saveToStorage(normalized);
        }
      } catch {
        // API unavailable, use localStorage data
      } finally {
        if (mounted) setLoading(false);
      }
    };
    const fetchCount = async () => {
      try {
        const data = await net.get('/pipeline/leads/available-count');
        if (mounted && typeof data?.count === 'number') {
          setAvailableCount(data.count);
        }
      } catch {
        // Count unavailable, leave as null
      }
    };
    fetchLeads();
    fetchCount();
    return () => { mounted = false; };
  }, []);

  // Persist to localStorage as cache
  useEffect(() => {
    saveToStorage(leads);
  }, [leads]);

  const addLead = useCallback(async (leadData) => {
    const now = new Date().toISOString();
    const initialHistory = {
      from: null,
      to: leadData.stage || 'lead',
      changedBy: userId,
      date: now,
    };

    const newLead = {
      phone: leadData.phone || '',
      name: leadData.name || '',
      zalo: leadData.zalo || '',
      email: leadData.email || '',
      source: leadData.source || '',
      priority: leadData.priority || 'medium',
      notes: leadData.notes || '',
      stage: leadData.stage || 'lead',
      addedBy: leadData.addedBy || userId,
      testDate: leadData.testDate || '',
      testTime: leadData.testTime || '',
      testLocation: leadData.testLocation || '',
      testStatus: leadData.testStatus || '',
      placementResult: leadData.placementResult || '',
      children: leadData.children || [],
      contacts: [],
      statusHistory: [initialHistory],
      createdAt: now,
      updatedAt: now,
    };

    // Optimistic update
    const tempId = generateId();
    const optimisticLead = { ...newLead, id: tempId, _id: tempId };
    setLeads(prev => [...prev, optimisticLead]);

    try {
      const data = await net.post('/pipeline/leads', newLead);
      if (data?.lead) {
        const saved = normalizeLead(data.lead);
        setLeads(prev => prev.map(l => l.id === tempId ? saved : l));
        return saved;
      }
    } catch {
      // Keep optimistic version
    }
    return optimisticLead;
  }, [userId]);

  const updateLead = useCallback(async (id, updates) => {
    const now = new Date().toISOString();
    setLeads(prev => prev.map(lead =>
      (lead.id === id || lead._id === id)
        ? { ...lead, ...updates, updatedAt: now }
        : lead
    ));

    try {
      await net.patch(`/pipeline/leads/${id}`, updates);
    } catch {
      // Optimistic already applied
    }
  }, []);

  const deleteLead = useCallback(async (id) => {
    setLeads(prev => prev.filter(lead => (lead.id !== id && lead._id !== id)));

    try {
      await net.del(`/pipeline/leads/${id}`);
    } catch {
      // Already removed locally
    }
  }, []);

  const moveLead = useCallback(async (leadId, newStage) => {
    const now = new Date().toISOString();

    setLeads(prev => prev.map(lead => {
      if (lead.id !== leadId && lead._id !== leadId) return lead;
      const historyEntry = {
        from: lead.stage,
        to: newStage,
        changedBy: userId,
        date: now,
      };
      return {
        ...lead,
        stage: newStage,
        statusHistory: [...(lead.statusHistory || []), historyEntry],
        updatedAt: now,
      };
    }));

    try {
      await net.patch(`/pipeline/leads/${leadId}/stage`, { stage: newStage, changedBy: userId });
    } catch {
      // Optimistic already applied
    }
  }, [userId]);

  const addContact = useCallback(async (leadId, contact) => {
    const now = new Date().toISOString();
    const newContact = {
      ...contact,
      id: generateId(),
      date: now,
    };

    setLeads(prev => prev.map(lead => {
      if (lead.id !== leadId && lead._id !== leadId) return lead;
      return {
        ...lead,
        contacts: [...(lead.contacts || []), newContact],
        updatedAt: now,
      };
    }));

    try {
      const data = await net.post(`/pipeline/leads/${leadId}/contacts`, contact);
      if (data?.contact) {
        setLeads(prev => prev.map(lead => {
          if (lead.id !== leadId && lead._id !== leadId) return lead;
          return {
            ...lead,
            contacts: lead.contacts.map(c =>
              c.id === newContact.id ? { ...data.contact, id: data.contact._id || data.contact.id } : c
            ),
          };
        }));
      }
    } catch {
      // Optimistic already applied
    }
    return newContact;
  }, []);

  const checkPhone = useCallback(async (phone) => {
    try {
      const data = await net.get(`/pipeline/check-phone?phone=${encodeURIComponent(phone)}`);
      return data;
    } catch {
      // Fallback: check local leads
      const matches = leads.filter(l => l.phone === phone);
      return { exists: matches.length > 0, leads: matches };
    }
  }, [leads]);

  /**
   * Pulls the next best marketing lead into the pipeline.
   * Calls POST /api/pipeline/leads/next and adds the returned lead locally.
   *
   * @returns {Object|null} The normalized lead on success, or null if none available/error
   * @author Forge, opencode glm-5.1
   * @version 1.0
   * @since 2026-04-16
   */
  const pullNextLead = useCallback(async () => {
    try {
      const response = await fetch('/api/pipeline/leads/next', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + (localStorage.getItem('apiKey') || ''),
        },
      });

      if (response.status === 201) {
        const { lead } = await response.json();
        if (lead) {
          const normalized = normalizeLead(lead);
          setLeads(prev => [normalized, ...prev]);
          setAvailableCount(prev => (typeof prev === 'number' ? Math.max(0, prev - 1) : prev));
          return normalized;
        }
      } else if (response.status === 404) {
        setAvailableCount(0);
        return null;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const convertLead = useCallback(async (id, conversionData) => {
    const now = new Date().toISOString();
    setLeads(prev => prev.map(lead => {
      if (lead.id !== id && lead._id !== id) return lead;
      return {
        ...lead,
        ...conversionData,
        converted: true,
        stage: 'enrol',
        updatedAt: now,
      };
    }));

    try {
      await net.patch(`/pipeline/leads/${id}`, { ...conversionData, converted: true });
    } catch {
      // Optimistic already applied
    }
  }, []);

  const filteredLeads = useMemo(() => {
    const active = leads.filter(l => !l.converted);
    if (!searchQuery.trim()) return active;
    const q = searchQuery.toLowerCase();

    const phoneMatches = [];
    const otherMatches = [];

    active.forEach(lead => {
      if (lead.phone?.toLowerCase().includes(q)) {
        phoneMatches.push(lead);
      } else if (
        lead.name?.toLowerCase().includes(q) ||
        lead.zalo?.toLowerCase().includes(q) ||
        lead.email?.toLowerCase().includes(q) ||
        lead.source?.toLowerCase().includes(q) ||
        lead.notes?.toLowerCase().includes(q) ||
        lead.testLocation?.toLowerCase().includes(q) ||
        lead.placementResult?.toLowerCase().includes(q) ||
        lead.children?.some(child =>
          child.name?.toLowerCase().includes(q) ||
          child.currentLevel?.toLowerCase().includes(q)
        )
      ) {
        otherMatches.push(lead);
      }
    });

    return [...phoneMatches, ...otherMatches];
  }, [leads, searchQuery]);

  const stats = useMemo(() => {
    const active = leads.filter(l => !l.converted);
    const byStage = {};
    STAGES.forEach(s => { byStage[s.id] = 0; });
    active.forEach(lead => {
      if (byStage[lead.stage] !== undefined) byStage[lead.stage]++;
    });
    return {
      total: active.length,
      byStage,
      conversionRate: leads.length > 0
        ? Math.round((leads.filter(l => l.converted).length / leads.length) * 100)
        : 0,
    };
  }, [leads]);

  return {
    leads,
    filteredLeads,
    stages: STAGES,
    addLead,
    updateLead,
    deleteLead,
    moveLead,
    addContact,
    checkPhone,
    pullNextLead,
    convertLead,
    stats,
    searchQuery,
    setSearchQuery,
    loading,
    availableCount,
  };
}
